import { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Phase 4b — fetch shared-availability projections for the given owner set.
 *
 * Calls `getSharedAvailabilityProjection` (the sole legal viewer→owner
 * data crossing) for every ownerId and aggregates the results. Re-runs
 * when the active set changes; stale results for owners dropped from the
 * set are pruned.
 *
 * Inputs:
 *   pollId   : string | null — informational; included in audit logs only.
 *                              Pass null in create flow (no saved poll yet).
 *   ownerIds : string[]      — active overlay owners (caller-controlled).
 *
 * Outputs:
 *   marks       : Array<Mark & { ownerId }>      — marks pre-tagged with ownerId
 *   busyWindows : Array<BusyWindow & { ownerId }> — same for opaque busy windows
 *   ownersById  : { [ownerId]: { displayName, avatarUrl } }
 *   loading     : boolean — true while ANY owner's projection is in-flight
 *   errors      : { [ownerId]: Error } — per-owner error map; surfaced for
 *                                        toast/log handling, not blocking
 */
export function useSharedAvailabilityOverlays({ pollId, ownerIds }) {
  const [projectionsByOwner, setProjectionsByOwner] = useState({});
  const [errorsByOwner, setErrorsByOwner] = useState({});
  const [loading, setLoading] = useState(false);

  const ownerKey = (ownerIds || []).slice().sort().join("|");

  useEffect(() => {
    if (!ownerIds || ownerIds.length === 0) {
      setProjectionsByOwner({});
      setErrorsByOwner({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const callable = httpsCallable(
      getFunctions(),
      "getSharedAvailabilityProjection",
    );
    // Only forward `pollId` when it's a non-empty string. Create-flow callers
    // pass null and the CF treats absence as "draft" mode.
    const callArgs = (ownerId) =>
      typeof pollId === "string" && pollId
        ? { ownerId, pollId }
        : { ownerId };

    Promise.allSettled(
      ownerIds.map((ownerId) =>
        callable(callArgs(ownerId)).then((res) => ({
          ownerId,
          projection: res.data,
        })),
      ),
    ).then((settled) => {
      if (cancelled) return;
      const next = {};
      const nextErrors = {};
      for (const result of settled) {
        if (result.status === "fulfilled") {
          next[result.value.ownerId] = result.value.projection;
        } else {
          // The rejected promise carries the ownerId by virtue of the
          // index — but Promise.allSettled doesn't expose it directly.
          // Fall back to scanning the original list.
          const i = settled.indexOf(result);
          const ownerId = ownerIds[i];
          if (ownerId) nextErrors[ownerId] = result.reason;
        }
      }
      // Prune projections for owners no longer in the active set.
      const filtered = {};
      for (const ownerId of ownerIds) {
        if (next[ownerId]) filtered[ownerId] = next[ownerId];
      }
      setProjectionsByOwner(filtered);
      setErrorsByOwner(nextErrors);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // ownerKey captures the deduped+sorted set identity; including ownerIds
    // directly would re-fire on every render even when content is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId, ownerKey]);

  // Flatten into per-owner-tagged arrays the calendar consumer can merge.
  // Memoized on `projectionsByOwner` so identity is stable across renders that
  // don't change the underlying projections — downstream `useMemo` chains in
  // CreateSchedulerPage feed `eventsInView` inside useCalendarNavigation, and
  // an unstable identity here cascaded into a setScrollToTime feedback loop
  // that crashed week/day view with React #185.
  const { marks, busyWindows, ownersById } = useMemo(() => {
    const nextMarks = [];
    const nextBusyWindows = [];
    const nextOwnersById = {};
    for (const [ownerId, projection] of Object.entries(projectionsByOwner)) {
      if (!projection) continue;
      nextOwnersById[ownerId] = {
        displayName: projection.ownerDisplayName || null,
        avatarUrl: projection.ownerAvatarUrl || null,
      };
      for (const m of projection.marks || []) {
        nextMarks.push({ ...m, ownerId });
      }
      for (const bw of projection.busyWindows || []) {
        nextBusyWindows.push({ ...bw, ownerId });
      }
    }
    return {
      marks: nextMarks,
      busyWindows: nextBusyWindows,
      ownersById: nextOwnersById,
    };
  }, [projectionsByOwner]);

  return { marks, busyWindows, ownersById, loading, errors: errorsByOwner };
}
